import { useState } from "react";
import useScrollReveal from "@/hooks/useScrollReveal";

interface FinalCTAProps {
  tagline: string;
  title: string;
  subtitle: string;
  backgroundImage: string;
  formUrl: string;
}

export default function FinalCTA({
  tagline,
  title,
  subtitle,
  backgroundImage,
  formUrl,
}: FinalCTAProps) {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);
  const leftRef = useScrollReveal<HTMLDivElement>();
  const rightRef = useScrollReveal<HTMLDivElement>();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const desc = form.querySelector<HTMLTextAreaElement>('[name="vehicle_description"]');
    if (desc && desc.value.length > 500) {
      return;
    }
    setStatus("submitting");
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      const res = await fetch(formUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setStatus(res.ok ? "success" : "error");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section className="relative py-28 px-6 lg:px-14 overflow-hidden">
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url('${backgroundImage}')` }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-charcoal/97 via-charcoal/90 to-charcoal/75" />
      {/* Gold top edge */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-gold/60 via-gold/30 to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left — copy */}
          <div ref={leftRef} className="sr-hidden">
            <div className="flex items-center gap-4 mb-6">
              <span className="gold-rule" />
              <p className="text-gold font-sans text-xs font-bold uppercase tracking-[0.22em]">{tagline}</p>
            </div>

            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white font-bold mb-5 leading-tight">
              {title}
            </h2>
            <p className="text-stone-400 font-sans text-base leading-relaxed mb-10">
              {subtitle}
            </p>

            {/* Contact block */}
            <div className="flex flex-col gap-4 mb-10">
              <a
                href="tel:360-204-4747"
                className="inline-flex items-center gap-4 group cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-gold/20 border border-gold/40 text-gold group-hover:bg-gold group-hover:text-white transition-all">
                  <i className="ri-phone-fill text-lg"></i>
                </div>
                <div>
                  <p className="text-white font-sans font-bold text-lg group-hover:text-gold transition-colors">360-204-4747</p>
                  <p className="text-stone-500 font-sans text-xs">Click to Call — Available Now</p>
                </div>
              </a>
              <a
                href="https://maps.app.goo.gl/fb7msCbj7DyRFWRr9"
                target="_blank"
                rel="nofollow noreferrer"
                className="inline-flex items-center gap-4 group cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-stone-700/60 border border-stone-600 text-stone-400 group-hover:text-gold transition-all">
                  <i className="ri-map-pin-line text-lg"></i>
                </div>
                <div>
                  <p className="text-stone-300 font-sans font-semibold text-sm group-hover:text-white transition-colors">709 Shore Dr, Bremerton, WA</p>
                  <p className="text-stone-600 font-sans text-xs">Serving Kitsap County &amp; Puget Sound</p>
                </div>
              </a>
            </div>

            {/* Social */}
            <div className="flex items-center gap-3">
              <a
                href="https://www.facebook.com/media/set/?set=a.1213616161983207&type=3"
                target="_blank"
                rel="nofollow noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full border border-stone-600 text-stone-400 hover:border-gold hover:text-gold transition-colors cursor-pointer"
                aria-label="Facebook"
              >
                <i className="ri-facebook-fill text-sm"></i>
              </a>
              <a
                href="https://www.instagram.com/glendavisrodshots/"
                target="_blank"
                rel="nofollow noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full border border-stone-600 text-stone-400 hover:border-gold hover:text-gold transition-colors cursor-pointer"
                aria-label="Instagram @glendavisrodshots"
              >
                <i className="ri-instagram-line text-sm"></i>
              </a>
              <span className="text-stone-600 font-sans text-xs ml-2">@glendavisrodshots</span>
            </div>
          </div>

          {/* Right — form */}
          <div ref={rightRef} className="sr-hidden sr-delay-2">
            <div className="bg-white rounded-lg p-8 lg:p-10">
              {status === "success" ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-stone-100 text-gold mx-auto mb-5">
                    <i className="ri-check-line text-3xl"></i>
                  </div>
                  <h3 className="font-display text-charcoal text-2xl font-bold mb-3">Inquiry Received</h3>
                  <p className="text-stone-500 font-sans text-sm leading-relaxed">
                    Glen will be in touch shortly. For faster response, call{" "}
                    <a href="tel:360-204-4747" className="text-gold font-bold">360-204-4747</a>.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="font-display text-charcoal text-2xl font-bold mb-1">Request a Quote</h3>
                  <p className="text-stone-400 font-sans text-xs mb-7 leading-relaxed">
                    Tell us about your vehicle. Glen will respond with pricing and availability.
                  </p>

                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    action={formUrl}
                    method="POST"
                    className="flex flex-col gap-4"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-stone-600 font-sans text-xs font-semibold mb-1.5 block uppercase tracking-wide">Name *</label>
                        <input
                          name="name"
                          type="text"
                          required
                          placeholder="Your name"
                          className="w-full border border-stone-200 rounded-md px-4 py-3 text-sm font-sans text-stone-800 outline-none focus:border-gold transition-colors bg-stone-50 focus:bg-white"
                        />
                      </div>
                      <div>
                        <label className="text-stone-600 font-sans text-xs font-semibold mb-1.5 block uppercase tracking-wide">Phone *</label>
                        <input
                          name="phone"
                          type="tel"
                          required
                          placeholder="Your phone"
                          className="w-full border border-stone-200 rounded-md px-4 py-3 text-sm font-sans text-stone-800 outline-none focus:border-gold transition-colors bg-stone-50 focus:bg-white"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-stone-600 font-sans text-xs font-semibold mb-1.5 block uppercase tracking-wide">Email *</label>
                      <input
                        name="email"
                        type="email"
                        required
                        placeholder="your@email.com"
                        className="w-full border border-stone-200 rounded-md px-4 py-3 text-sm font-sans text-stone-800 outline-none focus:border-gold transition-colors bg-stone-50 focus:bg-white"
                      />
                    </div>
                    <div>
                      <label className="text-stone-600 font-sans text-xs font-semibold mb-1.5 block uppercase tracking-wide">Vehicle &amp; Project Details *</label>
                      <textarea
                        name="vehicle_description"
                        required
                        rows={4}
                        maxLength={500}
                        placeholder="Year, make, model — and what you're looking for..."
                        className="w-full border border-stone-200 rounded-md px-4 py-3 text-sm font-sans text-stone-800 outline-none focus:border-gold transition-colors resize-none bg-stone-50 focus:bg-white"
                        onChange={(e) => setCharCount(e.target.value.length)}
                      />
                      <p className={`text-xs font-sans text-right mt-1 ${charCount > 480 ? "text-red-400" : "text-stone-400"}`}>
                        {charCount}/500
                      </p>
                    </div>
                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="w-full py-4 bg-charcoal text-white font-sans font-bold text-xs uppercase tracking-widest rounded-md hover:bg-gold transition-all disabled:opacity-60 cursor-pointer whitespace-nowrap"
                    >
                      {status === "submitting" ? "Sending Inquiry..." : "Send My Inquiry — It's Free"}
                    </button>
                    <p className="text-stone-400 font-sans text-xs text-center">
                      Or call directly:{" "}
                      <a href="tel:360-204-4747" className="text-gold font-bold">360-204-4747</a>
                    </p>
                    {status === "error" && (
                      <p className="text-red-400 text-xs font-sans text-center">
                        Something went wrong. Please call 360-204-4747 directly.
                      </p>
                    )}
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
